import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  CartItem,
  addItem,
  removeItem,
  decreaseQuantity,
  clearCart,
} from "../store/cartSlice";
import { RootState } from "../store/store";
import { db } from "../firebase-config";
import { collection, getDocs } from "firebase/firestore";
import {
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined"; // Import the cart icon
import { useNavigate } from 'react-router-dom';


const CartComponent: React.FC = () => {
  const userId = useSelector((state: RootState) => state.user.userId);
  const [firestoreItems, setFirestoreItems] = useState<CartItem[]>([]);
  const items = useSelector((state: RootState) => state.cart.items);
  const [message, setMessage] = useState<string>("");
  const dispatch = useDispatch();
  const navigate = useNavigate();


  const fetchItems = async (userId: string) => {
    const querySnapshot = await getDocs(collection(db, "items")); // Change "items" to your Firestore collection name
    const itemsArray: CartItem[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const variants = data.variant || [];
      const item: CartItem = {
        id: doc.id,
        name: data.name,
        type: data.type,
        isActive: data.is_active,
        isSaleActive: data.is_sale_active,
        selectedVariant: variants[0],
      };
      if (item.isActive) {
        itemsArray.push(item);
      }
    });
    setFirestoreItems(itemsArray); // Set items to local state
  };

  useEffect(() => {
    if (userId) {
      fetchItems(userId);
    }
  }, [userId]);

  const handleAddItem = (item: CartItem) => {
    // Find the existing item in Redux state
    const existingItem = items.find(
      (cartItem) =>
        cartItem.id === item.id && cartItem.selectedVariant.id === item.selectedVariant.id
    );
    // Get the current quantity from Redux state
    const currentQuantity = existingItem?.selectedVariant.quantity || 0;
    if (currentQuantity < item.selectedVariant.availableQuantity) {
      // Prepare the updated item with an increment of 1
      const updatedItem: CartItem = {
        ...item,
        selectedVariant: {
          ...item.selectedVariant,
          quantity: currentQuantity + 1, // Increment by 1
        },
      };
      // Dispatch the action to add the item
      dispatch(addItem(updatedItem));
    }
  };

  // Decrease the quantity of a specific variant in the cart
  const handleDecreaseQuantity = (itemId: string, variantId: string) => {
    // Dispatch action with both itemId and variantId
    dispatch(decreaseQuantity({ itemId, variantId }));
    setMessage(""); // Clear any messages after the action
  };

  // Remove a specific variant from the cart
  const removeItems = (itemId: string, variantId: string) => {
    // Dispatch action with both itemId and variantId to remove the variant
    dispatch(removeItem({ itemId, variantId }));
    setMessage(""); // Clear any messages after the action
  };

  const totalPrice = items.reduce((total, item) => {
    const price = item.isSaleActive && item.selectedVariant.salePrice
      ? item.selectedVariant.salePrice
      : item.selectedVariant.unitPrice;
    return total + price * item.selectedVariant.quantity;
  }, 0);

  return (
    <div style={{ padding: "20px" }}>
      <Typography variant="h4" gutterBottom>
        Your Cart{" "}
        <ShoppingCartOutlinedIcon
          sx={{ fontSize: { xs: "28px", sm: "30px", md: "35px" }, marginLeft: 1, verticalAlign: "middle" }}
        />
      </Typography>
      <Grid container spacing={2}>
        {items.map((item) => (
          <Grid item xs={12} key={item.id + "_" + item.selectedVariant.color}>
            <Paper
              sx={{ padding: 2, backgroundColor: "rgba(255, 255, 255, 0.4)" }}
            >
              <Grid container spacing={2} alignItems="center">
                {/* Product Image */}
                <Grid item xs={2}>
                  <Box
                    component="img"
                    src={item.selectedVariant.imageUrl}
                    alt={item.name}
                    sx={{
                      width: { xs: "70px", sm: "100px", md: "150px" }, // Larger image size
                      height: "auto",
                      borderRadius: 2,
                      objectFit: "contain", // Ensures the image fits inside without cropping
                    }}
                  />
                </Grid>

                {/* Product Details */}
                <Grid item xs={6} sx={{ marginLeft: { xs: "25px", sm: "0" } }}>
                  <Typography variant="h6" sx={{ marginBottom: 1, fontSize: { xs: "0.6rem", sm: "0.9rem" } }}>
                    {item.name}
                  </Typography>
                  {item.selectedVariant.size && (
                    <Typography variant="h3" sx={{ marginRight: 2, fontSize: { xs: "0.5rem", sm: "0.8rem" } }}>
                      Size: {item.selectedVariant.size}
                    </Typography>
                  )}
                  <Typography variant="h3" sx={{ fontSize: { xs: "0.5rem", sm: "0.8rem" } }}>Color: {item.selectedVariant.color}</Typography>
                   <Typography variant="h3" sx={{ fontSize: { xs: "0.5rem", sm: "0.8rem" } }}>
                   Unit Price:&nbsp; 
                    {item.isSaleActive && item.selectedVariant.salePrice ? (
                      <>
                        <span style={{ textDecoration: "line-through", marginRight: "10px" }}>
                        LKR {item.selectedVariant.unitPrice.toFixed(2)}
                        </span>
                        <span style={{ color: "red" }}>
                        LKR {item.selectedVariant.salePrice.toFixed(2)}
                        </span>
                      </>
                    ) : (
                      `LKR ${item.selectedVariant.unitPrice.toFixed(2)}`
                    )}
                  </Typography>
                  <Typography variant="h3" sx={{ fontSize: { xs: "0.5rem", sm: "0.8rem" } }}>
                    Quantity: {item.selectedVariant.quantity}
                  </Typography>
                  {/* Error Message */}
                  {item.selectedVariant.quantity >= item.selectedVariant.availableQuantity && (
                    <Typography variant="body2" color="error" sx={{ marginTop: 1, fontSize: { xs: "0.5rem", sm: "0.8rem" } }}>
                      Maximum stock reached for {item.name}.
                    </Typography>)}
                </Grid>

                <Grid item xs={12} sm={4} sx={{ textAlign: "right", display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                  <Tooltip title="Increase Quantity" arrow>
                    <span>
                      <IconButton
                        color="primary"
                        onClick={() => handleAddItem(item)}
                        sx={{ marginRight: { xs: 0.7, sm: 0.8, md: 1 } }}
                        disabled={item.selectedVariant.quantity >= item.selectedVariant.availableQuantity}
                      >
                        <AddIcon sx={{ fontSize: { xs: "0.7rem", sm: "1.1rem", md: "1.5rem" } }} />
                      </IconButton>
                    </span>
                  </Tooltip>

                  <Typography
                    sx={{
                      display: "inline",
                      marginX: 1,
                      fontSize: { xs: "0.7rem", sm: "0.9rem" },
                      color: "#57b7fb",
                    }}
                  >
                    {item.selectedVariant.quantity}
                  </Typography>

                  <Tooltip title="Decrease Quantity" arrow>
                    <IconButton
                      color="primary"
                      onClick={() => handleDecreaseQuantity(item.id, item.selectedVariant.id)}
                      sx={{ marginRight: 1 }}
                    >
                      <RemoveIcon sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.5rem" } }} />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Remove Item" arrow>
                    <IconButton
                      color="secondary"
                      onClick={() => removeItems(item.id, item.selectedVariant.id)}
                    >
                      <DeleteIcon sx={{ fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.5rem" } }} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Typography variant="h3" sx={{ marginTop: 3, fontSize: { xs: "0.9rem", sm: "1.0rem", md: "1.3rem" } }}>
        Total Price: LKR {totalPrice.toFixed(2)}
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={() => dispatch(clearCart())}
        sx={{ marginTop: 2, color: "#fff", fontWeight: "600", fontSize: { xs: "0.6rem", sm: "0.8rem" } }}
      >
        Clear Cart
      </Button>
      <Button
        variant="contained"
        color="secondary"
        sx={{ marginTop: 2, marginLeft: 2, color: "#fff", fontWeight: "600", fontSize: { xs: "0.6rem", sm: "0.8rem" } }}
        onClick={() => navigate('/checkout')}
        disabled={items.length === 0} // Disable button if cart is empty
      >
        Proceed to Checkout
      </Button>
    </div>
  );
};

export default CartComponent;
